<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">الاشتراكات</h1>
          <p class="ds-page-header__subtitle">إدارة اشتراكات الطلاب ومتابعة حالتها وتواريخها</p>
        </div>
        <div class="ds-page-header__actions">
          <button class="ds-btn ds-btn--primary ds-btn--lg" @click="openCreate">
            <i class="pi pi-plus"></i> اشتراك يدوي
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter">
        <div class="filter__header">
          <div class="filter__header-icon"><i class="pi pi-filter"></i></div>
          <p class="filter__title">عوامل التصفية</p>
        </div>
        <div class="ds-field-grid ds-field-grid--4">
          <div class="form-field">
            <label class="field-label">الحالة</label>
            <select class="form-select" v-model="store.filters.status" @change="reload">
              <option value="">الكل</option>
              <option v-for="(lbl, key) in statusOptions" :key="key" :value="key">{{ lbl }}</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">الخطة</label>
            <select class="form-select" v-model="store.filters.plan_type" @change="reload">
              <option value="">الكل</option>
              <option v-for="t in planTypes" :key="t" :value="t">{{ planLabel(t) }}</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">النوع</label>
            <select class="form-select" v-model="store.filters.type" @change="reload">
              <option value="">الكل</option>
              <option value="academic_year">سنة دراسية</option>
              <option value="course">كورس</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">رقم الطالب</label>
            <div class="d-flex gap-2">
              <input type="number" class="form-control" v-model="store.filters.student_id" @keyup.enter="reload" placeholder="ID" dir="ltr" />
              <button class="ds-btn ds-btn--secondary" style="padding:0.5625rem 0.875rem" v-tooltip.top="'إعادة تعيين'" @click="resetFilters">
                <i class="pi pi-refresh"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <Loading v-if="store.loading" />

      <template v-else>
        <!-- Empty state -->
        <div v-if="!store.subscriptions.length" class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-id-card"></i></div>
          <h3 class="empty-state__title">لا توجد اشتراكات</h3>
          <p class="empty-state__desc">لم يتم العثور على اشتراكات مطابقة، جرّب تعديل عوامل التصفية أو أنشئ اشتراكاً يدوياً.</p>
          <button class="ds-btn ds-btn--primary empty-state__action" @click="openCreate">
            <i class="pi pi-plus"></i> اشتراك يدوي
          </button>
        </div>

        <template v-else>
          <!-- Desktop / tablet table -->
          <div class="ds-table-wrap table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th class="ps-3">الطالب</th>
                  <th>المنتج</th>
                  <th>الخطة</th>
                  <th class="text-center">السعر</th>
                  <th class="text-center">الحالة</th>
                  <th>يبدأ</th>
                  <th>ينتهي</th>
                  <th class="text-center pe-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in store.subscriptions" :key="s.id" :class="{ 'ds-row-highlight': s.id === highlightId }">
                  <td class="ps-3">
                    <div class="fw-bold">{{ s.student?.name || '—' }}</div>
                    <small class="text-muted">{{ s.student?.phone }}</small>
                  </td>
                  <td>{{ productLabel(s) }}</td>
                  <td>{{ planLabel(s.plan_type) }}</td>
                  <td class="text-center"><PriceBadge :value="s.price" /></td>
                  <td class="text-center"><span class="status-badge" :class="statusTone(s.status)">{{ statusLabel(s.status) }}</span></td>
                  <td><small>{{ date(s.starts_at) }}</small></td>
                  <td><small>{{ date(s.expires_at) }}</small></td>
                  <td class="text-center pe-3">
                    <div class="icon-action-group justify-content-center">
                      <button v-if="s.status !== 'active'" class="icon-action-btn icon-action-btn--success" v-tooltip.top="'تفعيل'" @click="activate(s)">
                        <i class="pi pi-check-circle"></i>
                      </button>
                      <button class="icon-action-btn icon-action-btn--neutral" v-tooltip.top="'تجديد يدوي'" @click="renew(s)">
                        <i class="pi pi-refresh"></i>
                      </button>
                      <button v-if="!['cancelled','expired'].includes(s.status)" class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'إلغاء'" @click="cancel(s)">
                        <i class="pi pi-ban"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile card list -->
          <div class="mobile-list">
            <div v-for="s in store.subscriptions" :key="s.id" class="mobile-list-card" :class="{ 'ds-row-highlight': s.id === highlightId }">
              <div class="mobile-list-card__top">
                <div class="mobile-list-card__meta">
                  <div class="mobile-list-card__title">{{ s.student?.name || '—' }}</div>
                  <small class="text-muted">{{ s.student?.phone }}</small>
                </div>
                <span class="status-badge" :class="statusTone(s.status)">{{ statusLabel(s.status) }}</span>
              </div>
              <div class="mobile-list-card__facts">
                <span>المنتج: <b>{{ productLabel(s) }}</b></span>
                <span>الخطة: <b>{{ planLabel(s.plan_type) }}</b></span>
                <span>السعر: <b><PriceBadge :value="s.price" /></b></span>
                <span>يبدأ: <b>{{ date(s.starts_at) }}</b></span>
                <span>ينتهي: <b>{{ date(s.expires_at) }}</b></span>
              </div>
              <div class="mobile-list-card__actions">
                <button v-if="s.status !== 'active'" class="ds-btn ds-btn--secondary ds-btn--sm" @click="activate(s)">
                  <i class="pi pi-check-circle"></i> تفعيل
                </button>
                <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="renew(s)">
                  <i class="pi pi-refresh"></i> تجديد
                </button>
                <button v-if="!['cancelled','expired'].includes(s.status)" class="ds-btn ds-btn--secondary ds-btn--sm" @click="cancel(s)">
                  <i class="pi pi-ban"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-3" v-if="store.pagination && store.pagination.last_page > 1">
            <pagination :data="store.pagination" @pagination-change-page="store.fetchSubscriptions" class="pagination mb-0" />
          </div>
        </template>
      </template>
    </div>
  </div>

  <!-- Manual create dialog (quick action) -->
  <Dialog v-model:visible="createOpen" modal :style="{ width: '36rem' }" :breakpoints="{ '640px': '95vw' }" header="اشتراك يدوي">
    <div class="ds-field-grid">
      <div class="form-field ds-field--full">
        <label class="field-label">الطالب <span class="required">*</span></label>
        <AutoComplete
          v-model="selectedStudent"
          :suggestions="studentSuggestions"
          optionLabel="name"
          dropdown
          forceSelection
          :delay="300"
          placeholder="ابحث بالاسم أو رقم الهاتف أو رقم الطالب..."
          class="w-100 student-ac"
          @complete="searchStudents"
          @item-select="onStudentSelect"
          @clear="clearStudent"
        >
          <template #option="{ option }">
            <div class="student-opt">
              <div class="student-opt__name">{{ option.name }}</div>
              <div class="student-opt__meta"><i class="pi pi-id-card"></i> ID: {{ option.id }}</div>
              <div class="student-opt__meta"><i class="pi pi-phone"></i> {{ option.phone || '—' }}</div>
            </div>
          </template>
        </AutoComplete>
        <span v-if="createForm.student_id" class="field-hint field-hint--success">
          <i class="pi pi-check-circle"></i> تم اختيار الطالب رقم {{ createForm.student_id }}
        </span>
      </div>
      <div class="form-field">
        <label class="field-label">النوع</label>
        <select class="form-select" v-model="createForm.type" @change="createForm.subscribable_id = ''">
          <option value="academic_year">سنة دراسية</option>
          <option value="course">كورس</option>
        </select>
      </div>
      <div class="form-field">
        <label class="field-label">المنتج</label>
        <select class="form-select" v-model="createForm.subscribable_id">
          <option value="" disabled>اختر...</option>
          <option v-for="p in productOptions" :key="p.id" :value="p.id">{{ p.label }}</option>
        </select>
      </div>
      <div class="form-field">
        <label class="field-label">الخطة</label>
        <select class="form-select" v-model="createForm.plan_type">
          <option v-for="t in planTypes" :key="t" :value="t">{{ planLabel(t) }}</option>
        </select>
      </div>
      <div class="form-field d-flex align-items-end">
        <label class="settings-row">
          <span class="settings-row__title">تفعيل مباشرة</span>
          <InputSwitch v-model="createForm.activate" />
        </label>
      </div>
      <div class="form-field ds-field--full">
        <label class="field-label">ملاحظات <span class="optional">اختياري</span></label>
        <textarea class="form-control" rows="2" v-model="createForm.notes"></textarea>
      </div>
    </div>
    <template #footer>
      <button class="btn-cancel" @click="createOpen = false">إلغاء</button>
      <button class="btn-submit" :disabled="store.acting || !createForm.student_id || !createForm.subscribable_id" @click="submitCreate">
        <i class="pi pi-spin pi-spinner-dotted" v-if="store.acting"></i>
        <i class="pi pi-check" v-else></i>
        {{ store.acting ? 'جارٍ الإنشاء...' : 'إنشاء الاشتراك' }}
      </button>
    </template>
  </Dialog>
</template>

<script>
import axios from 'axios';
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import AutoComplete from 'primevue/autocomplete';
import pagination from 'laravel-vue-pagination';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import PriceBadge from '@/views/courses/PriceBadge.vue';
import { useSubscriptionStore } from '@/stores/useSubscriptionStore.js';
import { useCourseStore } from '@/stores/useCourseStore.js';
import {
  PLAN_TYPES, STATUS_LABELS, planLabel, statusLabel, statusTone, typeLabel,
  formatMoney, formatDate, apiError,
} from '@/composables/subscriptionMeta.js';

const TYPE_FROM_MORPH = { Course: 'course', AcademicYear: 'academic_year' };

export default {
  name: 'SubscriptionsIndex',
  components: { Dialog, InputSwitch, AutoComplete, pagination, Breadcrumb, Loading, PriceBadge },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'الاشتراكات', path: '/subscriptions' }],
      planTypes: PLAN_TYPES,
      statusOptions: STATUS_LABELS,
      createOpen: false,
      createForm: { student_id: '', type: 'course', subscribable_id: '', plan_type: 'monthly', activate: false, notes: '' },
      selectedStudent: null,
      studentSuggestions: [],
      highlightId: null,
      academicYears: [],
    };
  },
  computed: {
    store() { return useSubscriptionStore(); },
    courseStore() { return useCourseStore(); },
    productOptions() {
      if (this.createForm.type === 'course') {
        return this.courseStore.courses.map(c => ({ id: c.id, label: c.title }));
      }
      return this.academicYears.map(y => ({ id: y.id, label: y.name }));
    },
  },
  methods: {
    planLabel, statusLabel, statusTone,
    money: formatMoney,
    date: formatDate,
    productLabel(s) {
      const p = s.subscribable;
      const name = p?.name || p?.title || '';
      const type = typeLabel(s.subscribable_type);
      return name ? `${name} (${type})` : type || '—';
    },
    reload() { this.store.fetchSubscriptions(1); },
    resetFilters() {
      Object.assign(this.store.filters, { status: '', plan_type: '', type: '', student_id: '' });
      this.store.fetchSubscriptions(1);
    },
    async activate(s) {
      const res = await Swal.fire({
        title: 'تفعيل الاشتراك', text: 'سيتم احتساب تاريخ الانتهاء تلقائياً حسب الخطة.',
        icon: 'question', showCancelButton: true, confirmButtonText: 'تفعيل', cancelButtonText: 'إلغاء', confirmButtonColor: '#16a34a',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.activate(s.id);
        await this.store.fetchSubscriptions(this.store.pagination?.current_page || 1);
        Swal.fire({ icon: 'success', title: 'تم التفعيل', timer: 1200, showConfirmButton: false });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
    async cancel(s) {
      const res = await Swal.fire({
        title: 'إلغاء الاشتراك', icon: 'warning', showCancelButton: true,
        confirmButtonText: 'نعم، إلغاء', cancelButtonText: 'رجوع', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.cancel(s.id);
        await this.store.fetchSubscriptions(this.store.pagination?.current_page || 1);
        Swal.fire({ icon: 'success', title: 'تم الإلغاء', timer: 1200, showConfirmButton: false });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
    async renew(s) {
      const type = TYPE_FROM_MORPH[String(s.subscribable_type).split('\\').pop()] || 'course';
      const res = await Swal.fire({
        title: 'تجديد يدوي', text: 'سيتم إنشاء اشتراك جديد بنفس المنتج والخطة وتفعيله.',
        icon: 'question', showCancelButton: true, confirmButtonText: 'تجديد وتفعيل', cancelButtonText: 'إلغاء',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.create({
          student_id: s.student_id, type, subscribable_id: s.subscribable_id,
          plan_type: s.plan_type, activate: true,
        });
        await this.store.fetchSubscriptions(this.store.pagination?.current_page || 1);
        Swal.fire({ icon: 'success', title: 'تم التجديد', timer: 1200, showConfirmButton: false });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
    async searchStudents(event) {
      const q = (event.query || '').trim();
      if (!q) { this.studentSuggestions = []; return; }
      try {
        const { data } = await axios.get('/admin/students/search', { params: { q } });
        this.studentSuggestions = data.data ?? [];
      } catch { this.studentSuggestions = []; }
    },
    onStudentSelect(event) {
      const student = event.value;
      this.createForm.student_id = student?.id ?? '';
    },
    clearStudent() {
      this.selectedStudent = null;
      this.createForm.student_id = '';
    },
    async openCreate() {
      this.createForm = { student_id: '', type: 'course', subscribable_id: '', plan_type: 'monthly', activate: false, notes: '' };
      this.selectedStudent = null;
      this.studentSuggestions = [];
      this.createOpen = true;
      if (!this.courseStore.courses.length) this.courseStore.fetchCourses();
      if (!this.academicYears.length) {
        try { const { data } = await axios.get('/academic-years'); this.academicYears = data.data ?? []; } catch { /* non-fatal */ }
      }
    },
    async submitCreate() {
      try {
        const res = await this.store.create({
          student_id: this.createForm.student_id,
          type: this.createForm.type,
          subscribable_id: this.createForm.subscribable_id,
          plan_type: this.createForm.plan_type,
          notes: this.createForm.notes || null,
          activate: this.createForm.activate,
        });
        this.createOpen = false;
        // Auto-refresh the table + highlight the newly created row (no manual refresh).
        await this.store.fetchSubscriptions(1);
        this.highlightRow(res?.data?.id ?? null);
        Swal.fire({
          icon: 'success', title: 'تم إنشاء الاشتراك بنجاح', toast: true, position: 'top',
          timer: 2500, showConfirmButton: false,
        });
      } catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false }); }
    },
    highlightRow(id) {
      if (!id) return;
      this.highlightId = id;
      if (this._hlTimer) clearTimeout(this._hlTimer);
      this._hlTimer = setTimeout(() => { this.highlightId = null; }, 4000);
    },
  },
  mounted() {
    this.store.fetchSubscriptions(1);
  },
  beforeUnmount() {
    if (this._hlTimer) clearTimeout(this._hlTimer);
  },
};
</script>

<style scoped>
.student-opt { display: flex; flex-direction: column; gap: 2px; padding: 2px 0; }
.student-opt__name { font-weight: 700; color: var(--text-primary); }
.student-opt__meta { font-size: 0.78rem; color: var(--text-secondary); display: flex; align-items: center; gap: 0.3rem; }
.student-ac :deep(.p-autocomplete) { width: 100%; }
.student-ac :deep(.p-inputtext) { width: 100%; }
.field-hint { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.78rem; margin-top: 0.4rem; }
.field-hint--success { color: var(--color-success); }
.settings-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; width: 100%; cursor: pointer; }
.settings-row__title { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); }
</style>
